/** Saved identity survives restarts, offline requests and failed renewal until manual logout. */
import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import type { EntitlementDto } from '@appbuilder/shared';
import { authApi, configureClient, type LoginRequest, type SignupRequest } from '../api/client';
import { readSession, writeSession, createSessionRenewer, SESSION_KEY, type Session, type SessionUser } from './session';

export type AuthUser = SessionUser;

interface AuthState {
  user: AuthUser | null | undefined;
  entitlement: EntitlementDto | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (req: LoginRequest) => Promise<void>;
  signup: (req: SignupRequest) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const sessionRef = useRef<Session | null>(readSession());
  const [token, setToken] = useState<string | null>(sessionRef.current?.accessToken ?? null);
  const [user, setUser] = useState<AuthUser | null | undefined>(sessionRef.current?.user ?? undefined);
  const [entitlement, setEntitlement] = useState<EntitlementDto | null>(null);

  const saveSession = useCallback((next: Session | null) => {
    sessionRef.current = next;
    writeSession(next);
    setToken(next?.accessToken ?? null);
    setUser(next?.user ?? null);
  }, []);

  const renew = useMemo(() => createSessionRenewer(() => sessionRef.current, saveSession, authApi.refresh), [saveSession]);

  const loadMe = useCallback(async () => {
    const start = sessionRef.current;
    if (!start) return;
    const me = await authApi.me();
    const current = sessionRef.current;
    if (!current || (current.accessToken !== start.accessToken &&
      (!start.refreshToken || current.refreshToken !== start.refreshToken))) return;
    saveSession({ ...current, user: me.user });
    setEntitlement(me.entitlement);
  }, [saveSession]);

  // Wire before child screens issue passive-effect requests for the restored account.
  useLayoutEffect(() => {
    configureClient(() => sessionRef.current?.accessToken ?? null, renew, () => sessionRef.current?.refreshToken ?? null);
  }, [renew]);

  useEffect(() => {
    if (!sessionRef.current) { setUser(null); return; }
    loadMe().catch(() => setUser(sessionRef.current?.user ?? null));
  }, [loadMe]);

  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key !== SESSION_KEY && event.key !== null) return;
      const next = readSession();
      sessionRef.current = next;
      setToken(next?.accessToken ?? null);
      setUser(next?.user ?? null);
      setEntitlement(null);
    };
    const retry = () => { if (sessionRef.current) void loadMe().catch(() => {}); };
    window.addEventListener('storage', sync);
    window.addEventListener('online', retry);
    return () => { window.removeEventListener('storage', sync); window.removeEventListener('online', retry); };
  }, [loadMe]);

  const login = useCallback(async (req: LoginRequest) => {
    const res = await authApi.login(req);
    setEntitlement(null);
    saveSession({ ...res.tokens, user: res.user });
    await loadMe().catch(() => {});
  }, [saveSession, loadMe]);

  const signup = useCallback(async (req: SignupRequest) => {
    const res = await authApi.signup(req);
    setEntitlement(null);
    saveSession({ ...res.tokens, user: res.user });
    await loadMe().catch(() => {});
  }, [saveSession, loadMe]);

  const logout = useCallback(() => {
    const refreshToken = sessionRef.current?.refreshToken;
    saveSession(null);
    setEntitlement(null);
    if (refreshToken) void authApi.logout(refreshToken).catch(() => {});
  }, [saveSession]);

  const refreshMe = useCallback(async () => { if (sessionRef.current) await loadMe(); }, [loadMe]);
  const value = useMemo<AuthState>(() => ({
    user, entitlement, token, isAuthenticated: Boolean(token), login, signup, logout, refreshMe,
  }), [user, entitlement, token, login, signup, logout, refreshMe]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
